import DropdownField from '@polestar/component-warehouse-react/components/dropdown-field/DropdownField';
import FormField from '@polestar/component-warehouse-react/components/form-field/FormField';
import Row from '@polestar/component-warehouse-react/components/row/Row';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import TextField from '@polestar/component-warehouse-react/components/text-field/TextField';
import styled from '@emotion/styled';
import { MainAxisAlignments } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import React, { forwardRef, Fragment, useCallback, useMemo, useRef, useState } from 'react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { useContainerFocusTriggers } from '@polestar/component-warehouse-react/hooks/useContainerFocusTriggers';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useWidthCallback } from '@polestar/component-warehouse-react/hooks/useDimensions';
import { css } from '@emotion/react';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { findCountryCode } from './PhoneNumberField.helpers';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
const PhoneNumberField = forwardRef(({ countryCodeOptions, style, value, name, disabled, required, label, error, errorMessage, hint, trailing, onChange, onBlur, onFocus, tooltip, ...props }, ref) => {
    const internalRef = useRef(null);
    const combinedRef = useCombinedRef(ref, internalRef);
    const memoizedId = useSSRSafeId('phone-number-field', props.id);
    const memoizedFieldId = useMemo(() => `${memoizedId}-wrapper`, [memoizedId]);
    const countryCode = value === null || value === void 0 ? void 0 : value.countryCode;
    const phoneNumber = (value === null || value === void 0 ? void 0 : value.value) || '';
    const countryListRef = useRef(null);
    const { isRTL } = useDirection();
    const [callbackRef, popoverWidth] = useWidthCallback('offsetWidth');
    const handleKeyPress = useCallback((e) => {
        // prevent keys other than 0-9, spaces and `-` to be put in
        const result = new RegExp(/[0-9\-\ ].*/gim).exec(e.key);
        if (result === null) {
            preventCancelableDefault(e);
        }
    }, []);
    const handleChange = useCallback((val) => {
        var _a;
        // Handle the case where the value starts with three or more zeros; we don't want to auto update the
        // country code when this happens.
        const hasLeadingZeros = /^0{3,}/.test(val);
        if (hasLeadingZeros) {
            onChange === null || onChange === void 0 ? void 0 : onChange({ countryCode, value: val }, name);
            return;
        }
        const countryCodeInput = findCountryCode(countryCodeOptions, val);
        const hasMultipleCountryCodes = (countryCodeInput === null || countryCodeInput === void 0 ? void 0 : countryCodeInput.length) > 1;
        const hasCountryCode = !!countryCodeInput && (countryCodeInput === null || countryCodeInput === void 0 ? void 0 : countryCodeInput.length) > 0;
        if (hasMultipleCountryCodes) {
            // open up the country code because there is more than 1 match, so let the user decided
            // only open up here, because a country code change will also result in a new change in the "tel-national"-textfield
            (_a = countryListRef === null || countryListRef === void 0 ? void 0 : countryListRef.current) === null || _a === void 0 ? void 0 : _a.click();
        }
        onChange === null || onChange === void 0 ? void 0 : onChange({
            countryCode: hasMultipleCountryCodes
                ? undefined
                : hasCountryCode
                    ? countryCodeInput[0]
                    : countryCode,
            value: hasCountryCode
                ? val.replace(new RegExp(`.*(\\+${hasMultipleCountryCodes ? countryCodeInput[0] : countryCodeInput})(\\s{1})?`, 'gi'), '')
                : val,
        }, name);
    }, [onChange, countryCode, name, countryCodeOptions]);
    const handleCountryCodeChange = useCallback((val) => {
        var _a;
        // put focus to next part of this field
        (_a = internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        const countryCodeInput = findCountryCode(countryCodeOptions, val);
        const hasMultipleCountryCodes = (countryCodeInput === null || countryCodeInput === void 0 ? void 0 : countryCodeInput.length) > 1;
        const hasCountryCode = !!countryCodeInput && (countryCodeInput === null || countryCodeInput === void 0 ? void 0 : countryCodeInput.length) > 0;
        onChange === null || onChange === void 0 ? void 0 : onChange({
            countryCode: hasMultipleCountryCodes
                ? undefined
                : hasCountryCode
                    ? countryCodeInput[0]
                    : val,
            value: hasCountryCode
                ? val.replace(new RegExp(`.*(\\+${hasMultipleCountryCodes ? countryCodeInput[0] : countryCodeInput})(\\s{1})?`, 'gi'), '')
                : phoneNumber,
        }, name);
    }, [onChange, phoneNumber, name, countryCodeOptions]);
    const [optionsListVisible, setOptionsListVisible] = useState(false);
    const { focusBlurEvents } = useContainerFocusTriggers({
        name,
        fakeFocus: optionsListVisible,
        onBlur,
        onFocus,
    });
    const handleOptionsListToggled = useCallback((visible) => {
        setOptionsListVisible(visible);
    }, []);
    const formatValueLabel = useCallback((option) => option ? `+ ${option.countryCode || option.value}` : '+ ', []);
    const renderOption = useCallback((option) => (React.createElement(CountryCodeOption, { mainAxisAlignment: MainAxisAlignments.spaceBetween },
        React.createElement(TextWrapper, null, option === null || option === void 0 ? void 0 : option.label),
        React.createElement(Spacer, { orientation: Orientations.horizontal, spacing: Spacings.large }),
        React.createElement(TextWrapper, null,
            "+",
            (option === null || option === void 0 ? void 0 : option.countryCode) || (option === null || option === void 0 ? void 0 : option.value)))), []);
    return (React.createElement(StyledFormField, { ref: callbackRef, ...focusBlurEvents, id: memoizedFieldId, prefixId: memoizedId, label: label, required: required, hint: hint, error: error, errorMessage: errorMessage || (typeof error === 'string' && error), isTextInput: false, border: false, disabled: disabled, tooltip: tooltip },
        React.createElement(StyledRow, { isRTL: isRTL },
            countryCodeOptions && (React.createElement(Fragment, null,
                React.createElement(StyledDropdownField, { ...aria({
                        labelledby: !!label ? `${memoizedId}-label` : undefined,
                        describedby: !!hint || !!error ? `${memoizedId}-message` : undefined,
                    }), ref: countryListRef, options: countryCodeOptions, whitelistedOptionLabels: ['label', 'countryCode'], value: countryCode, disabled: disabled, name: `${name}-country-code`, id: `${memoizedId}-country-code`, onChange: handleCountryCodeChange, formatValueLabel: formatValueLabel, fieldProps: {
                        style: { minWidth: `3.5rem`, flex: 0 },
                        alwaysActive: true,
                    }, autoComplete: `tel-country-code`, type: "tel", renderOption: renderOption, popoverWidth: popoverWidth, onPopoverToggled: handleOptionsListToggled }),
                React.createElement(Spacer, { orientation: Orientations.horizontal }))),
            React.createElement(TextField, { ...aria({
                    labelledby: !!label ? `${memoizedId}-label` : undefined,
                    describedby: !!hint || !!error ? `${memoizedId}-message` : undefined,
                }), autoComplete: !!countryCodeOptions ? `tel-national` : 'tel', ...props, type: "tel", ref: combinedRef, id: memoizedId, name: name, value: phoneNumber, onKeyPress: handleKeyPress, onChange: handleChange, fieldProps: {
                    style: { flex: 1, ...style },
                }, disabled: disabled, trailing: trailing }))));
});
PhoneNumberField.displayName = 'PhoneNumberField';
export default PhoneNumberField;
const TextWrapper = styled.span `
  min-width: 0;

  /* CountryCode options should always be ltr */
  direction: ltr;
`;
// ensure both fields have an error state
const StyledFormField = styled(FormField)(({ theme, errorMessage, error }) => css((errorMessage || error) &&
    css `
        [data-id='border'] {
          background-color: ${theme.formField.states.error.borderColor};
        }
      `));
const StyledDropdownField = styled(DropdownField)(({ theme, value }) => 
// only meant to ensure that default `+` value is not 100% Colors.black
css `
      & + div > label {
        color: ${value === undefined || value === ''
    ? theme.formField.states.enabled.labelColor
    : 'inherit'};

        /* CountryCode should always be ltr */
        direction: ltr;
      }
    `);
// necessary for tests
const StyledRow = styled(Row)(({ isRTL }) => css(css `
      flex: 1;
    `, isRTL &&
    css `
        flex-direction: row-reverse;

        input {
          direction: rtl;
        }
      `));
// necessary for tests
const CountryCodeOption = styled(Row) `
  flex-grow: 1;
`;
